// Pure helpers for the Team Detail per-role section view (PRODOPSAI-531;
// per-role sections matching the PDF org chart added in PRODOPSAI-543). Kept
// outside the component modules so they stay unit-testable and the components
// stay flat (S3776 / S7721).

import type {
  ITeamMembershipTreePerson,
  ITeamMembershipTreeRoleGroup,
} from '@skystone/shared';
import { roleAbbreviation } from './team-identity';

/** Stable React key for a role group (catalog id, or synthetic entra key). */
export function roleGroupKey(group: ITeamMembershipTreeRoleGroup): string {
  return group.roleId ?? `entra:${group.displayName.toLowerCase()}`;
}

/** "1 role" / "N roles". */
export function roleCountLabel(count: number): string {
  return count === 1 ? '1 role' : `${count} roles`;
}

// ── Per-role sections (PRODOPSAI-543) ─────────────────────────────────────────
//
// The PDF org chart is the source of truth: one section per team role
// (Engineering Manager / Lead Engineer / Engineer / Front-end Engineer /
// Solution Architect / …). Sections preserve the backend ordering (sortOrder
// then displayName), so map-preserving-order is enough.

export interface SectionMember {
  person: ITeamMembershipTreePerson;
  /** The role implied by the parent section header. */
  roleDisplayName: string;
}

export interface RoleSection {
  /** Slug derived from the role displayName (e.g. 'engineering_manager'). */
  id: string;
  /** Role displayName from the catalog or entra-title. */
  label: string;
  /** Backend sort order, preserved so sections render in PDF order. */
  order: number;
  /** Whether the role comes from the catalog or a free-form Entra title. */
  source: 'catalog' | 'entra-title';
  /** Compact role code for the small badge (EM, ENG, FE …). */
  abbrev: string;
  members: SectionMember[];
}

/**
 * Slugify a role displayName: 'Engineering Manager' → 'engineering_manager'.
 *
 * Trim leading and trailing underscores with two anchored replaces; the
 * alternation + quantifier combo (`/^_+|_+$/g`) tripped SC S5852 on PR #679
 * iter 1, so we keep them as separate anchored regexes (no backtracking
 * opportunity, behaviour-identical).
 */
export function slugify(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+/, '')
    .replace(/_+$/, '');
}

/**
 * Map the backend's flat role groups to per-role sections, preserving order.
 * The backend already sorts roleGroups by sortOrder then displayName, so a
 * simple ordered map is sufficient.
 */
export function groupByRole(
  roleGroups: readonly ITeamMembershipTreeRoleGroup[],
): RoleSection[] {
  return roleGroups.map((group, index) => ({
    id: slugify(group.displayName),
    label: group.displayName,
    order: group.sortOrder ?? index * 100,
    source: group.source,
    abbrev: roleAbbreviation(group.displayName),
    members: group.persons.map((p) => ({
      person: p,
      roleDisplayName: group.displayName,
    })),
  }));
}

/**
 * Whether to render a member's Entra job title as a secondary line.
 * - `entra-title` sections: the heading already IS the title → never repeated.
 * - `catalog` sections: shown only when the title differs from the role name
 *   (case-insensitive, trimmed).
 */
export function shouldShowEntraTitle(
  section: Pick<RoleSection, 'source'>,
  member: SectionMember,
): boolean {
  const title = member.person.jobTitle?.trim() ?? '';
  if (title === '') return false;
  if (section.source === 'entra-title') return false;
  return title.toLowerCase() !== member.roleDisplayName.trim().toLowerCase();
}

/** Initials of the first Engineering Manager, for the header meta ("EM: JS"). */
export function engineeringManagerInitials(
  roleGroups: readonly ITeamMembershipTreeRoleGroup[],
): string | null {
  for (const group of roleGroups) {
    if (group.displayName.trim().toLowerCase() === 'engineering manager' && group.persons.length > 0) {
      const name = group.persons[0].displayName.trim();
      const parts = name.split(/\s+/).slice(0, 2);
      return parts.map((p) => (Array.from(p)[0] ?? '').toUpperCase()).join('') || null;
    }
  }
  return null;
}
